import {
  isCompleteExtraction,
  parseSystemLine,
  parseUnimedText,
  type ExtractedProposal,
} from '@sales-rules';
import { isAxiosError, isCancel } from 'axios';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { errorMessage } from '../../lib/api';
import { readPdfText } from '../../lib/pdf-text';
import { dmy, salesApi } from '../../lib/sales';

type Tone = '' | 'warn' | 'bad';

/**
 * Área de PDF do formulário (V2). O pdf.js lê o texto no navegador e o leitor da Unimed
 * tenta fechar os dados; se não fechar (outro modelo, escaneado), o PDF vai para a IA no
 * servidor. "Parar leitura" cancela. Também aceita colar a linha do resultado do sistema.
 * O PDF fica anexado à proposta ao salvar.
 */
export function PdfZone({
  fill,
  attached,
  initialFile,
}: {
  /** Preenche o formulário; devolve o que não achou. `file` fica para anexar ao salvar. */
  fill: (data: ExtractedProposal, file?: File) => string[];
  attached: string | null;
  initialFile: File | null;
}) {
  const [status, setStatus] = useState<{ text: ReactNode; tone: Tone }>({
    text: attached
      ? `PDF anexado: ${attached}. Anexe outro para reler os dados.`
      : 'O sistema lê o PDF e preenche os campos. Depois é só dizer a situação de cada pessoa.',
    tone: '',
  });
  const [reading, setReading] = useState(false);
  const [done, setDone] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pasted, setPasted] = useState('');
  const abort = useRef<AbortController | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const finish = (file: File, data: ExtractedProposal) => {
    const missing = fill(data, file);
    const people = data.beneficiaries?.length ?? 0;
    if (data.beneficiaries?.some((person) => person.monthlyCents === null))
      missing.push('algum valor');
    setDone(true);
    setStatus({
      tone: missing.length ? 'warn' : '',
      text: (
        <>
          Li <b>{file.name}</b>: {people} {people === 1 ? 'beneficiário' : 'beneficiários'}.
          Confira os campos
          {missing.length ? ` (não achei: ${missing.join(', ')})` : ''} e marque a situação de
          cada pessoa.
        </>
      ),
    });
  };

  const read = async (file: File | undefined) => {
    if (!file) return;
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) {
      setStatus({ text: 'Esse arquivo não é um PDF.', tone: 'bad' });
      return;
    }
    setReading(true);
    setDone(false);
    setStatus({
      text: (
        <>
          Lendo <b>{file.name}</b>…
        </>
      ),
      tone: '',
    });
    const controller = new AbortController();
    abort.current = controller;
    try {
      let text = '';
      try {
        text = await readPdfText(file);
      } catch {
        // Sem o texto (PDF estranho ou o leitor não carregou), a IA ainda pode ler.
      }
      const direct = parseUnimedText(text);
      if (isCompleteExtraction(direct)) {
        finish(file, direct);
        return;
      }
      setStatus({
        text: (
          <>
            Extraindo os dados de <b>{file.name}</b>. Leva uns 20 a 60 segundos…
          </>
        ),
        tone: '',
      });
      const data = await salesApi.extractPdf(file, controller.signal);
      finish(file, data);
    } catch (error) {
      if (isCancel(error) || controller.signal.aborted) {
        fill({}, file);
        setStatus({
          text: 'Leitura interrompida. O PDF fica anexado; preencha os campos à mão.',
          tone: '',
        });
        return;
      }
      // Mesmo sem ler, o PDF fica anexado à proposta.
      fill({}, file);
      const status = isAxiosError(error) ? error.response?.status : undefined;
      setStatus({
        tone: 'bad',
        text:
          status === 503
            ? 'A leitura de PDFs de outros modelos ainda não está ligada. O PDF fica anexado; preencha à mão.'
            : errorMessage(
                error,
                'Não consegui ler esse PDF. Tente de novo ou preencha à mão.',
              ),
      });
    } finally {
      abort.current = null;
      setReading(false);
    }
  };

  // O PDF arrastado para a tela já começa a ser lido.
  const started = useRef(false);
  useEffect(() => {
    if (initialFile && !started.current) {
      started.current = true;
      void Promise.resolve().then(() => read(initialFile));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile]);
  useEffect(() => () => abort.current?.abort(), []);

  const paste = (value: string) => {
    setPasted(value);
    const line = parseSystemLine(value);
    if (!line.requestNumber && !line.effectiveDate) return;
    fill({
      requestNumber: line.requestNumber,
      effectiveDate: line.effectiveDate,
      company: line.company,
      beneficiaries: line.holder ? [{ name: line.holder, relationship: 'TITULAR' }] : undefined,
    });
    setStatus({
      tone: '',
      text: (
        <>
          Preenchi a solicitação <b>{line.requestNumber ?? '—'}</b> e a vigência{' '}
          <b>{line.effectiveDate ? dmy(line.effectiveDate) : '—'}</b> a partir do resultado.
        </>
      ),
    });
  };

  return (
    <div
      className={`pdfzone${dragging ? ' drag' : ''}${done ? ' ok' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragging(false);
        void read(event.dataTransfer.files[0]);
      }}
    >
      <button
        className="btn ghost"
        type="button"
        disabled={reading}
        onClick={() => input.current?.click()}
      >
        {done || attached ? 'Trocar PDF' : 'Anexar PDF da proposta'}
      </button>
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          void read(file);
        }}
      />
      <div className={`pdfst${status.tone ? ` ${status.tone}` : ''}`}>{status.text}</div>
      {reading && (
        <>
          <div className="pdfbar">
            <span />
          </div>
          <button className="linkbtn" type="button" onClick={() => abort.current?.abort()}>
            Parar leitura
          </button>
        </>
      )}
      <label className="field" style={{ width: '100%' }}>
        <span>Ou cole a linha do resultado do sistema</span>
        <input
          type="text"
          placeholder="Solicitação: 23574 - Movimentação: … - Vigência: 30/10/2026"
          value={pasted}
          onChange={(event) => paste(event.target.value)}
        />
      </label>
    </div>
  );
}
